import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Popconfirm,
  message,
  Modal,
  Switch,
  Image,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  UndoOutlined,
  FlagOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productService, categoryService } from '../../services';
import API_CONFIG from '../../config/api';
import { formatCurrency, formatDate, debounce } from '../../utils/helpers';

const { Option } = Select;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    category_id: null,
    is_active: null,
    is_featured: null,
    is_deleted: false,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const navigate = useNavigate();

  // Helper function to check promotion status
  const getPromotionStatus = (promotionStart, promotionEnd) => {
    const now = new Date();
    
    // Trường hợp đặc biệt: cả promotion_start và promotion_end đều null 
    // => khuyến mại vô thời hạn, luôn có hiệu lực
    if (!promotionStart && !promotionEnd) {
      return { isValid: true, status: 'unlimited' };
    }
    
    // Nếu không có promotion_start thì không có khuyến mại
    if (!promotionStart) {
      return { isValid: false, status: 'no_promotion' };
    }
    
    const startDate = new Date(promotionStart);
    
    // Nếu chưa đến thời gian khuyến mại
    if (now < startDate) {
      return { isValid: false, status: 'not_started' };
    }
    
    // Nếu promotion_end là null thì không giới hạn thời hạn (từ promotion_start trở đi)
    if (!promotionEnd) {
      return { isValid: true, status: 'unlimited' };
    }
    
    const endDate = new Date(promotionEnd);
    
    // Kiểm tra có còn trong thời hạn không
    if (now <= endDate) {
      return { isValid: true, status: 'active' };
    } else {
      return { isValid: false, status: 'expired' };
    }
  };

  // Helper function to check if discount is still valid (backward compatibility)
  const isDiscountValid = (promotionStart, promotionEnd) => {
    return getPromotionStatus(promotionStart, promotionEnd).isValid;
  };

  // Helper function to render price with discount logic
  const renderPrice = (product, isMobileView = false) => {
    const hasDiscount = product.discount_price && parseFloat(product.discount_price) > 0;
    
    if (!hasDiscount) {
      return (
        <span style={{ 
          fontSize: isMobileView ? '16px' : '14px', 
          fontWeight: isMobileView ? 700 : 'normal',
          color: '#1890ff' 
        }}>
          {formatCurrency(product.price)}
        </span>
      );
    }

    const promotionStatus = getPromotionStatus(product.promotion_start, product.promotion_end);
    
    if (promotionStatus.isValid) {
      // Khuyến mại đang hoạt động
      return (
        <div>
          <div style={{ 
            fontSize: isMobileView ? '16px' : '14px', 
            fontWeight: isMobileView ? 700 : 600,
            color: '#ff4d4f',
            marginBottom: isMobileView ? '2px' : '1px'
          }}>
            {formatCurrency(product.discount_price)}
            {isMobileView && (
              <Tag size="small" color="red" style={{ marginLeft: '6px', fontSize: '10px' }}>
                {promotionStatus.status === 'unlimited' ? 'KM VÔ HẠN' : 'SALE'}
              </Tag>
            )}
          </div>
          <div style={{ 
            fontSize: isMobileView ? '12px' : '11px', 
            color: '#999',
            textDecoration: 'line-through'
          }}>
            {formatCurrency(product.price)}
          </div>
          {!isMobileView && (
            <div style={{ marginTop: '2px' }}>
              <Tag size="small" color="volcano" style={{ fontSize: '10px' }}>
                {Math.round(((product.price - product.discount_price) / product.price) * 100)}% GIẢM
              </Tag>
              {promotionStatus.status === 'unlimited' && (
                <Tag size="small" color="purple" style={{ fontSize: '10px', marginLeft: '4px' }}>
                  VÔ HẠN
                </Tag>
              )}
            </div>
          )}
        </div>
      );
    } else {
      // Khuyến mại không hoạt động
      let statusTag, statusText, statusColor;
      
      switch (promotionStatus.status) {
        case 'not_started':
          statusTag = 'CHƯA BẮT ĐẦU';
          statusText = 'Chưa đến thời gian khuyến mại';
          statusColor = 'orange';
          break;
        case 'expired':
          statusTag = 'HẾT HẠN';
          statusText = 'Khuyến mại đã hết hạn';
          statusColor = 'default';
          break;
        default:
          statusTag = 'HẾT HẠN';
          statusText = 'Khuyến mại đã hết hạn';
          statusColor = 'default';
      }
      
      return (
        <div>
          <div style={{ 
            fontSize: isMobileView ? '16px' : '14px', 
            fontWeight: isMobileView ? 700 : 600,
            color: '#1890ff',
            marginBottom: isMobileView ? '2px' : '1px'
          }}>
            {formatCurrency(product.price)}
            {isMobileView && (
              <Tag size="small" color={statusColor} style={{ marginLeft: '6px', fontSize: '10px' }}>
                {statusTag}
              </Tag>
            )}
          </div>
          <div style={{ 
            fontSize: isMobileView ? '12px' : '11px', 
            color: '#ccc',
            textDecoration: 'line-through'
          }}>
            {formatCurrency(product.discount_price)}
          </div>
          {!isMobileView && (
            <Tag size="small" color={statusColor} style={{ marginTop: '2px', fontSize: '10px' }}>
              {statusTag}
            </Tag>
          )}
        </div>
      );
    }
  };

  // Placeholder SVG for image fallback
  const placeholderSvg = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjBGMEYwIiBzdHJva2U9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTIwIDIwSDE1VjE1SDIwVjIwWk0zNSAzNUgxNVYzMEwyMCAyNUwyNSAzMEwzMCAyNUwzNSAzMFYzNVoiIGZpbGw9IiNCRkJGQkYiLz4KPHR4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5OTk5OTkiPk5vIEltYWdlPC90eHQ+Cjwvc3ZnPgo=";

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [pagination.current, pagination.pageSize, filters]);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
  // Map frontend filters to API params (chỉ dùng params API hỗ trợ)
  const params = {
        page: pagination.current,
        pageSize: pagination.pageSize, // backend expects pageSize
        // API chỉ hỗ trợ 3 params này
        nameFilter: filters.search || '',
        categoryIdFilter: filters.category_id || undefined,
      };

      // Xử lý isActiveFilter đặc biệt - convert boolean thành number
      if (filters.is_active !== null) {
        params.isActiveFilter = filters.is_active ? 1 : 0;
      }
      // Xử lý isDeletedFilter: gửi về backend để backend quyết định trả dữ liệu đã xóa hay không
      if (filters.is_deleted !== null && filters.is_deleted !== undefined) {
        params.isDeletedFilter = filters.is_deleted ? 1 : 0;
      }
      
      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });
      
      //console.log('🔍 Loading products with params:', params); // Debug: xem params gửi đi
      
      const response = await productService.getFull(params);
      //console.log('📦 API response:', response); // Debug: xem response trả về
      
      if (response.success) {
        let filteredProducts = response.data || [];
        
        // Client-side filtering cho các filter API không hỗ trợ
        if (filters.is_featured !== null) {
          //console.log('🌟 Client-side filtering by is_featured:', filters.is_featured);
          filteredProducts = filteredProducts.filter(product => 
            Boolean(product.is_featured) === Boolean(filters.is_featured)
          );
        }
        
        if (filters.is_deleted !== null && filters.is_deleted !== false) {
          //console.log('🗑️ Client-side filtering by is_deleted:', filters.is_deleted);
          filteredProducts = filteredProducts.filter(product => 
            Boolean(product.is_deleted) === Boolean(filters.is_deleted)
          );
        }
        
        //console.log('Products after filtering:', filteredProducts);
        //console.log('First product:', filteredProducts[0]);
        
        setProducts(filteredProducts);
        // If backend returns a total count, use it; otherwise fall back to page length
        setPagination(prev => ({
          ...prev,
          total: response.total !== undefined ? response.total : filteredProducts.length,
        }));
      } else {
        //console.error('API returned error:', response.message);
        message.error(response.message || 'Không thể tải danh sách sản phẩm');
      }
    } catch (error) {
      //console.error('Error loading products:', error);
      message.error('Không thể tải danh sách sản phẩm');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      //console.log('📂 Loading categories...'); // Debug
      const response = await categoryService.getAll();
      //console.log('📂 Categories response:', response); // Debug
      if (response.success) {
        //console.log('📂 Categories data:', response.data); // Debug
        setCategories(response.data || []);
      } else {
        //console.error('Categories API error:', response.message);
      }
    } catch (error) {
      //console.error('Error loading categories:', error);
    }
  };

  const handleSearch = debounce((value) => {
    //console.log('🔎 Search triggered with value:', value); // Debug
    setFilters(prev => {
      const newFilters = { ...prev, search: value };
      //console.log('🔎 New filters after search:', newFilters);
      return newFilters;
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  }, 500);

  const handleFilterChange = (key, value) => {
    //console.log('🎛️ Filter changed:', key, '=', value); // Debug
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      //console.log('🎛️ New filters after change:', newFilters);
      return newFilters;
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));
  };

  const handleDelete = async (productId) => {
    try {
      const response = await productService.delete(productId);
      if (response.success) {
        message.success('Xóa sản phẩm thành công');
        loadProducts();
      } else {
        message.error(response.message || 'Xóa sản phẩm thất bại');
      }
    } catch (error) {
      //console.error('Error deleting product:', error);
      message.error('Xóa sản phẩm thất bại');
    }
  };

  const handleRestore = async (productId) => {
    try {
      const response = await productService.restore(productId);
      if (response.success) {
        message.success('Khôi phục sản phẩm thành công');
        loadProducts();
      } else {
        message.error(response.message || 'Khôi phục sản phẩm thất bại');
      }
    } catch (error) {
      //console.error('Error restoring product:', error);
      message.error('Khôi phục sản phẩm thất bại');
    }
  };

  const handleToggleActive = async (productId, activeStatus) => {
    try {
      const response = await productService.toggleActive(productId, activeStatus);
      if (response.success) {
        message.success('Cập nhật trạng thái thành công');
        loadProducts();
      } else {
        message.error(response.message || 'Cập nhật trạng thái thất bại');
      }
    } catch (error) {
      //console.error('Error toggling active status:', error);
      message.error('Cập nhật trạng thái thất bại');
    }
  };

  const handleUpdateFlags = async (productId, flags) => {
    try {
      const accessToken = localStorage.getItem('accessToken') || '';
      //console.log('🔑 Access Token:', accessToken ? 'EXISTS' : 'MISSING');
      //console.log('🔑 Token length:', accessToken?.length);
      
      const body = {
        accessToken,
        isFeatured: flags.isFeatured,
        isNew: flags.isNew,
        isBestSeller: flags.isBestSeller
      };
      //console.log('🚀 Sending updateFlags request:', {
      //   productId,
      //   body
      // });
      const response = await productService.updateFlags(productId, body);
      //console.log('✅ UpdateFlags response:', response);
      if (response.success) {
        message.success('Cập nhật nhãn sản phẩm thành công');
        loadProducts();
      } else {
        message.error(response.message || 'Cập nhật nhãn sản phẩm thất bại');
      }
    } catch (error) {
      //console.error('❌ Error updating flags:', error);
      //console.error('❌ Error response:', error.response?.data);
      if (error.response?.data?.message?.includes('Token')) {
        message.error('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại!');
      } else {
        message.error('Cập nhật nhãn sản phẩm thất bại');
      }
    }
  };

  const columns = [
    {
      title: 'Hình ảnh',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 80,
      render: (imageUrl, record) => {
        // Debug: log image info
        //console.log('Image render - Full record:', record);
        //console.log('Image render - imageUrl:', imageUrl);
        //console.log('Available image fields:', {
        //   image_url: record.image_url,
        //   image: record.image,
        //   imageUrl: record.imageUrl,
        //   photo: record.photo,
        //   picture: record.picture,
        //   thumbnail: record.thumbnail
        // });
        
        // Try different field names that might contain image URL
        const imgSrc = imageUrl || record.image || record.imageUrl || record.photo || record.picture || record.thumbnail || null;

        // If product has an images array (from backend), prefer the first item
        const firstListImage = (record.images && record.images.length > 0)
          ? (record.images[0].url || record.images[0].image_url || record.images[0].path || record.images[0].file || record.images[0].image)
          : null;

        // Normalize URLs: if relative (/uploads/...), prefix with API base
        const normalizeUrl = (url) => {
          if (!url) return null;
          try {
            // absolute URL OK
            new URL(url);
            return url;
          } catch (e) {
            // relative path -> prefix with API base
            if (url.startsWith('/')) {
              return API_CONFIG.CURRENT_URL + url;
            }
            return url; // best-effort
          }
        };

        const finalImageSrc = normalizeUrl(imgSrc) || normalizeUrl(firstListImage) || null;
        
        return (
          <div style={{ position: 'relative' }}>
            <Image
              width={50}
              height={50}
              src={finalImageSrc}
              fallback={placeholderSvg}
              preview={true}
              placeholder={
                <div style={{ 
                  width: 50, 
                  height: 50, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  color: '#999',
                  fontSize: '10px'
                }}>
                  Loading...
                </div>
              }
              style={{ 
                objectFit: 'cover', 
                borderRadius: 4,
                border: '1px solid #d9d9d9',
                cursor: 'pointer'
              }}
              onError={(e) => {
                //console.log('❌ Image load FAILED for:', finalImageSrc);
                //console.log('Error details:', e);
              }}
              onLoad={() => {
                //console.log('✅ Image loaded SUCCESS:', finalImageSrc);
              }}
            />
            {!imgSrc && (
              <div style={{
                position: 'absolute',
                top: -5,
                right: -5,
                background: 'orange',
                color: 'white',
                borderRadius: '50%',
                width: 16,
                height: 16,
                fontSize: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                T
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            SKU: {record.sku || 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text) => text || 'Chưa phân loại',
    },
    {
      title: 'Giá',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => renderPrice(record, false),
      sorter: true,
    },
    {
      title: 'Nhãn',
      key: 'flags',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Space size="small">
            <Switch
              size="small"
              checked={record.is_featured}
              onChange={(checked) => handleUpdateFlags(record.id || record.product_id, {
                isFeatured: checked ? 1 : 0,
                isNew: record.is_new ? 1 : 0,
                isBestSeller: record.is_bestseller ? 1 : 0
              })}
            />
            <span style={{ fontSize: '12px' }}>Nổi bật</span>
          </Space>
          <Space size="small">
            <Switch
              size="small"
              checked={record.is_new}
              onChange={(checked) => handleUpdateFlags(record.id || record.product_id, {
                isFeatured: record.is_featured ? 1 : 0,
                isNew: checked ? 1 : 0,
                isBestSeller: record.is_bestseller ? 1 : 0
              })}
            />
            <span style={{ fontSize: '12px' }}>Mới</span>
          </Space>
          <Space size="small">
            <Switch
              size="small"
              checked={record.is_bestseller}
              onChange={(checked) => handleUpdateFlags(record.id || record.product_id, {
                isFeatured: record.is_featured ? 1 : 0,
                isNew: record.is_new ? 1 : 0,
                isBestSeller: checked ? 1 : 0
              })}
            />
            <span style={{ fontSize: '12px' }}>Bán chạy</span>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Space direction="vertical" size="small">
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? 'Hoạt động' : 'Tạm dừng'}
          </Tag>
          {record.is_deleted && (
            <Tag color="orange">Đã xóa</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
      sorter: true,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => {
                // Products sử dụng field 'id', không phải 'product_id'
                const productId = record.id || record.product_id || record.productId;
                //console.log('View product clicked, ID:', productId, 'Record:', record);
                navigate(`/products/view/${productId}`);
              }}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                //console.log('🔧 Edit clicked for record:', record);
                //console.log('🆔 Product ID:', record.product_id || record.id);
                const productId = record.product_id || record.id;
                navigate(`/products/edit/${productId}`);
              }}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? 'Tạm dừng' : 'Kích hoạt'}>
              <Button
              icon={<FlagOutlined />}
              size="small"
              onClick={() => {
                const productId = record.id || record.product_id || record.productId;
                const desiredStatus = !Boolean(record.is_active); // invert current
                //console.log('Toggle active clicked for productId:', productId, 'record:', record, 'desiredStatus:', desiredStatus);
                handleToggleActive(productId, desiredStatus);
              }}
              type={record.is_active ? 'default' : 'primary'}
            />
          </Tooltip>
          {record.is_deleted ? (
            <Tooltip title="Khôi phục">
              <Popconfirm
                title="Bạn có chắc chắn muốn khôi phục sản phẩm này?"
                onConfirm={() => {
                  const productId = record.id || record.product_id || record.productId;
                  if (!productId) {
                    message.error('Không xác định được sản phẩm để khôi phục');
                    return;
                  }
                  handleRestore(productId);
                }}
                okText="Có"
                cancelText="Không"
              >
                <Button icon={<UndoOutlined />} size="small" type="primary" />
              </Popconfirm>
            </Tooltip>
          ) : (
            <Tooltip title="Xóa">
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa sản phẩm này?"
                onConfirm={() => {
                  const productId = record.id || record.product_id || record.productId;
                  if (!productId) {
                    message.error('Không xác định được sản phẩm để xóa');
                    return;
                  }
                  handleDelete(productId);
                }}
                okText="Có"
                cancelText="Không"
              >
                <Button icon={<DeleteOutlined />} size="small" danger />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // Mobile Product Card Component
  const MobileProductCard = ({ product }) => (
    <Card
      size="small"
      style={{ 
        marginBottom: 12,
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
      bodyStyle={{ padding: 12 }}
    >
      <Row gutter={12} align="middle">
        <Col span={6}>
          <Image
            src={product.image_url || '/placeholder.png'}
            alt={product.name}
            width={60}
            height={60}
            style={{ 
              borderRadius: 8,
              objectFit: 'cover'
            }}
            preview={false}
            fallback="/placeholder.png"
          />
        </Col>
        <Col span={18}>
          <div>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 600, 
              marginBottom: 4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {product.name}
            </div>
            
            <div style={{ marginBottom: 8 }}>
              <Space size={4} wrap>
                <Tag color="blue" size="small">
                  {product.category_name || 'N/A'}
                </Tag>
                
                {product.is_active ? (
                  <Tag color="green" size="small">Hoạt động</Tag>
                ) : (
                  <Tag color="red" size="small">Tạm dừng</Tag>
                )}
                
                {product.is_featured && (
                  <Tag color="gold" size="small">Nổi bật</Tag>
                )}
                
                {product.is_new && (
                  <Tag color="cyan" size="small">Mới</Tag>
                )}
              </Space>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8
            }}>
              <div>
                {renderPrice(product, true)}
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Space size={4}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/products/view/${product.id}`)}
                >
                  Xem
                </Button>
                
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/products/edit/${product.id}`)}
                >
                  Sửa
                </Button>
                
                <Popconfirm
                  title="Xác nhận xóa"
                  description="Bạn có chắc chắn muốn xóa sản phẩm này?"
                  onConfirm={() => handleDelete(product.id)}
                  okText="Xóa"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Space>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* Bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Tìm kiếm sản phẩm..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Danh mục"
              value={filters.category_id}
              onChange={(value) => {
                //console.log('📂 Category selected:', value); // Debug
                handleFilterChange('category_id', value);
              }}
              allowClear
              style={{ width: '100%' }}
              loading={categories.length === 0}
              notFoundContent={categories.length === 0 ? 'Đang tải...' : 'Không có dữ liệu'}
            >
              {categories.map(category => {
                //console.log('📂 Rendering category:', category); // Debug
                // Categories sử dụng field 'id', không phải 'category_id'
                const categoryId = category.id || category.category_id || category.categoryId;
                const categoryName = category.name || category.category_name || category.title;
                
                return (
                  <Option key={categoryId} value={categoryId}>
                    {categoryName}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Trạng thái"
              value={filters.is_active}
              onChange={(value) => {
                //console.log('🔛 Active filter changed:', value, 'Type:', typeof value);
                //console.log('🔛 Will convert to:', value !== null ? (value ? 1 : 0) : null);
                handleFilterChange('is_active', value);
              }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Hoạt động</Option>
              <Option value={false}>Tạm dừng</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Nổi bật"
              value={filters.is_featured}
              onChange={(value) => {
                //console.log('🌟 Featured filter changed:', value);
                handleFilterChange('is_featured', value);
              }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Nổi bật</Option>
              <Option value={false}>Thường</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="Hiển thị"
              value={filters.is_deleted}
              onChange={(value) => {
                //console.log('🗑️ Deleted filter changed:', value);
                handleFilterChange('is_deleted', value);
              }}
              style={{ width: '100%' }}
            >
              <Option value={false}>Bình thường</Option>
              <Option value={true}>Đã xóa</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadProducts}
                loading={loading}
                title="Làm mới"
              />
              <Button
                onClick={() => {
                  //console.log('🧹 Clearing all filters');
                  setFilters({
                    search: '',
                    category_id: null,
                    is_active: null,
                    is_featured: null,
                    is_deleted: false,
                  });
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                title="Xóa bộ lọc"
              >
                Xóa lọc
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/products/add')}
              >
                Thêm mới
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Responsive Product Display */}
        {isMobile ? (
          // Mobile: Card Layout
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Hiển thị {products.length} sản phẩm
              </div>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => loadProducts()}
                loading={loading}
              >
                Tải lại
              </Button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Đang tải...</div>
              </div>
            ) : products.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
                <div>Không có sản phẩm nào</div>
              </div>
            ) : (
              products.map(product => (
                <MobileProductCard key={product.id} product={product} />
              ))
            )}

            {/* Mobile Pagination */}
            {pagination.total > pagination.pageSize && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: 16,
                padding: 16,
                borderTop: '1px solid #f0f0f0'
              }}>
                <Space direction="vertical" size={8}>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Trang {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
                    {' '}(Tổng: {pagination.total} sản phẩm)
                  </div>
                  <Space>
                    <Button
                      size="small"
                      disabled={pagination.current <= 1}
                      onClick={() => {
                        setPagination(prev => ({ ...prev, current: prev.current - 1 }));
                      }}
                    >
                      Trước
                    </Button>
                    <Button
                      size="small"
                      disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                      onClick={() => {
                        setPagination(prev => ({ ...prev, current: prev.current + 1 }));
                      }}
                    >
                      Sau
                    </Button>
                  </Space>
                </Space>
              </div>
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={products}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} sản phẩm`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>
    </div>
  );
};

export default ProductList;
